// src/components/WordleCalendar.js
import React, { useState, useEffect, useRef } from 'react';
import { fetchDislikedWords, addDislikedWord, removeDislikedWord } from '../services/wordleService';
import { formatDate, isPast, isToday } from '../utils/dateUtils';
import UiIcon from './ui/UiIcon';

const FINNISH_ALPHABET_REGEX = /^[A-ZÄÖÅ]*$/;

const Icon = ({ name }) => <UiIcon name={name} />;

const WordleCalendar = ({ puzzles, onSavePuzzle, wordsPerDay = 1 }) => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [words, setWords] = useState({});
  const [wordList, setWordList] = useState(null);
  const [isLoadingDict, setIsLoadingDict] = useState(false);
  const [dislikedWords, setDislikedWords] = useState(new Set());
  const [showDisliked, setShowDisliked] = useState(false);
  // Last dislike, kept so it can be undone: { word, dateStr, index }
  const [lastDislike, setLastDislike] = useState(null);
  const undoTimer = useRef(null);
  const currentMonthRef = useRef(null);
  // Past months stay collapsed until loaded (keys "year-month")
  const [loadedMonths, setLoadedMonths] = useState(new Set());
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();

  // Open on the current month instead of January
  useEffect(() => {
    currentMonthRef.current?.scrollIntoView({ block: 'start' });
  }, []);

  useEffect(() => {
    fetchDislikedWords()
      .then(setDislikedWords)
      .catch(error => console.error('Failed to load disliked words:', error));
  }, []);

  useEffect(() => {
    const initial = {};
    puzzles.forEach(p => {
      if (wordsPerDay === 1) {
        initial[p.id] = p.word || '';
      } else {
        // Multi-word mode (Quordle)
        if (typeof p.words === 'string' && p.words.trim()) {
          initial[p.id] = p.words.split(' ').filter(w => w).slice(0, wordsPerDay);
        } else if (Array.isArray(p.words)) {
          initial[p.id] = p.words.slice(0, wordsPerDay);
        } else {
          initial[p.id] = Array(wordsPerDay).fill('');
        }
        while (initial[p.id].length < wordsPerDay) initial[p.id].push('');
      }
    });
    setWords(initial);
  }, [puzzles, wordsPerDay]);

  const isCollapsed = (monthIndex) =>
    (year < currentYear || (year === currentYear && monthIndex < currentMonth)) &&
    !loadedMonths.has(`${year}-${monthIndex}`);

  const loadMonth = (monthIndex) =>
    setLoadedMonths(prev => new Set(prev).add(`${year}-${monthIndex}`));

  const getMonthDays = (monthIndex) => {
    const days = [];
    const lastDay = new Date(year, monthIndex + 1, 0).getDate();
    for (let d = 1; d <= lastDay; d++) {
      days.push(new Date(year, monthIndex, d));
    }
    return days;
  };

  const getStatus = (dateStr, wordData) => {
    if (wordsPerDay === 1) {
      if (!wordData) return 'empty';
      if (isToday(dateStr)) return 'today';
      if (isPast(dateStr)) return 'past';
      return 'ready';
    } else {
      const filledCount = wordData.filter(w => w && w.length === 5).length;
      if (filledCount === 0) return 'empty';
      if (isToday(dateStr)) return 'today';
      if (isPast(dateStr)) return 'past';
      return filledCount === wordsPerDay ? 'ready' : 'partial';
    }
  };

  const handleChange = (dateStr, value, index = 0) => {
    const upper = value.toUpperCase();
    if (value && !FINNISH_ALPHABET_REGEX.test(upper)) return;
    if (upper.length > 5) return;

    if (wordsPerDay === 1) {
      setWords(prev => ({ ...prev, [dateStr]: upper }));
    } else {
      setWords(prev => {
        const current = Array.isArray(prev[dateStr]) ? prev[dateStr] : Array(wordsPerDay).fill('');
        const updated = [...current];
        updated[index] = upper;
        return { ...prev, [dateStr]: updated };
      });
    }
  };

  const handleBlur = async (dateStr) => {
    if (wordsPerDay === 1) {
      const word = words[dateStr] || '';
      if (word.length === 5 || word.length === 0) {
        await onSavePuzzle({
          id: dateStr,
          word
        });
      }
    } else {
      const wordsArray = Array.isArray(words[dateStr]) ? words[dateStr] : Array(wordsPerDay).fill('');
      const allValid = wordsArray.every(w => !w || w.length === 0 || w.length === 5);
      if (allValid) {
        const nonEmptyWords = wordsArray.filter(w => w && w.trim());
        await onSavePuzzle({
          id: dateStr,
          words: nonEmptyWords.length > 0 ? nonEmptyWords.join(' ') : ''
        });
      }
    }
  };

  const loadDictionary = async () => {
    if (wordList) return wordList;

    setIsLoadingDict(true);
    try {
      const response = await fetch('/kotus_sanat_5.txt');
      const text = await response.text();
      const words = text
        .split('\n')
        .map(w => w.trim().toUpperCase())
        .filter(w => {
          if (w.length !== 5 || !FINNISH_ALPHABET_REGEX.test(w)) return false;
          // Filter out words with less than 4 unique letters
          const uniqueLetters = new Set(w.split('')).size;
          return uniqueLetters >= 4;
        });
      setWordList(words);
      return words;
    } catch (error) {
      console.error('Failed to load dictionary:', error);
      alert('Virhe sanakirjan lataamisessa');
      return [];
    } finally {
      setIsLoadingDict(false);
    }
  };

  const getUsedWordsLast3Months = () => {
    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
    threeMonthsAgo.setHours(0, 0, 0, 0);

    const used = [];
    puzzles.forEach(p => {
      const [day, month, year] = p.id.split('-');
      const puzzleDate = new Date(year, month - 1, day);
      if (puzzleDate >= threeMonthsAgo) {
        if (wordsPerDay === 1 && p.word) {
          used.push(p.word.toUpperCase());
        } else if (wordsPerDay > 1) {
          const wordsArray = typeof p.words === 'string'
            ? p.words.split(' ').filter(w => w)
            : (Array.isArray(p.words) ? p.words : []);
          used.push(...wordsArray.map(w => w.toUpperCase()));
        }
      }
    });
    return used;
  };

  const getAvailableWords = (dict, disliked = dislikedWords) => {
    const usedWords = new Set(getUsedWordsLast3Months());
    return dict.filter(word => !usedWords.has(word) && !disliked.has(word));
  };

  const handleRandomWord = async (dateStr, index = 0, disliked = dislikedWords) => {
    const dict = await loadDictionary();
    if (dict.length === 0) return;

    const availableWords = getAvailableWords(dict, disliked);

    if (availableWords.length === 0) {
      alert('Kaikki sanat on käytetty viimeisen 3 kuukauden aikana!');
      return;
    }

    const randomWord = availableWords[Math.floor(Math.random() * availableWords.length)];

    if (wordsPerDay === 1) {
      setWords(prev => ({ ...prev, [dateStr]: randomWord }));
      await onSavePuzzle({
        id: dateStr,
        word: randomWord
      });
    } else {
      const updated = await new Promise(resolve => {
        setWords(prev => {
          const current = Array.isArray(prev[dateStr]) ? prev[dateStr] : Array(wordsPerDay).fill('');
          const updated = [...current];
          updated[index] = randomWord;
          resolve(updated);
          return { ...prev, [dateStr]: updated };
        });
      });
      // Auto-save individual word changes to prevent data loss
      await onSavePuzzle({
        id: dateStr,
        words: updated.join(' ')
      });
    }
  };

  const handleRandomizeAll = async (dateStr) => {
    if (wordsPerDay === 1) return;

    const dict = await loadDictionary();
    if (dict.length === 0) return;

    const availableWords = getAvailableWords(dict);

    if (availableWords.length < wordsPerDay) {
      alert('Ei tarpeeksi käyttämättömiä sanoja!');
      return;
    }

    const selected = [];
    const available = [...availableWords];
    for (let i = 0; i < wordsPerDay; i++) {
      const idx = Math.floor(Math.random() * available.length);
      selected.push(available[idx]);
      available.splice(idx, 1);
    }

    setWords(prev => ({ ...prev, [dateStr]: selected }));
    await onSavePuzzle({
      id: dateStr,
      words: selected.join(' ')
    });
  };

  // Bans the current word from future randomizing and rolls a replacement.
  const handleDislike = async (dateStr, index = 0) => {
    const current = wordsPerDay === 1 ? words[dateStr] : words[dateStr]?.[index];
    if (!current || current.length !== 5) return;

    const nextDisliked = new Set(dislikedWords).add(current);
    setDislikedWords(nextDisliked);
    try {
      await addDislikedWord(current);
    } catch (error) {
      console.error('Failed to save disliked word:', error);
      alert('Virhe sanan hylkäämisessä');
      return;
    }
    setLastDislike({ word: current, dateStr, index });
    clearTimeout(undoTimer.current);
    undoTimer.current = setTimeout(() => setLastDislike(null), 15000);
    await handleRandomWord(dateStr, index, nextDisliked);
  };

  const handleRemoveDisliked = async (word) => {
    try {
      await removeDislikedWord(word);
    } catch (error) {
      console.error('Failed to remove disliked word:', error);
      alert('Virhe sanan palauttamisessa');
      return false;
    }
    setDislikedWords(prev => {
      const next = new Set(prev);
      next.delete(word);
      return next;
    });
    return true;
  };

  // Un-dislikes the last word and puts it back in its slot
  const handleUndoDislike = async () => {
    if (!lastDislike) return;
    const { word, dateStr, index } = lastDislike;
    clearTimeout(undoTimer.current);
    setLastDislike(null);
    if (!(await handleRemoveDisliked(word))) return;

    if (wordsPerDay === 1) {
      setWords(prev => ({ ...prev, [dateStr]: word }));
      await onSavePuzzle({ id: dateStr, word });
    } else {
      const current = Array.isArray(words[dateStr]) ? words[dateStr] : Array(wordsPerDay).fill('');
      const updated = [...current];
      updated[index] = word;
      setWords(prev => ({ ...prev, [dateStr]: updated }));
      await onSavePuzzle({ id: dateStr, words: updated.join(' ') });
    }
  };

  useEffect(() => () => clearTimeout(undoTimer.current), []);

  const months = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

  // One word field with its roll / dislike actions (actions only on editable days)
  const renderWord = (dateStr, value, idx, canRandomize) => (
    <div key={idx} className="wd-word">
      <input
        type="text"
        className="wd-input"
        value={value}
        onChange={(e) => handleChange(dateStr, e.target.value, idx)}
        onBlur={() => handleBlur(dateStr)}
        onKeyDown={(e) => e.key === 'Enter' && e.target.blur()}
        placeholder={wordsPerDay === 1 ? 'SANA' : '·····'}
        maxLength={5}
        spellCheck={false}
      />
      {canRandomize && (
        <div className="wd-actions">
          {/* Always rendered so the dice never shifts when a word appears */}
          <button
            type="button"
            className={`wd-btn wd-dislike ${value.length === 5 ? '' : 'is-hidden'}`}
            onClick={() => handleDislike(dateStr, idx)}
            disabled={isLoadingDict || value.length !== 5}
            title="En pidä tästä sanasta: älä arvo sitä enää"
            aria-label="Hylkää sana"
          >
            <Icon name="thumb_down" />
          </button>
          <button
            type="button"
            className="wd-btn wd-dice"
            onClick={() => handleRandomWord(dateStr, idx)}
            disabled={isLoadingDict}
            title="Satunnainen sana"
            aria-label="Satunnainen sana"
          >
            <Icon name="casino" />
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="wd-container">
      <div className="wd-year-header">
        <button onClick={() => setYear(year - 1)} className="btn-secondary" aria-label="Previous year">
          <Icon name="chevron_left" /> {year - 1}
        </button>
        <h2>{year}</h2>
        <button onClick={() => setYear(year + 1)} className="btn-secondary" aria-label="Next year">
          {year + 1} <Icon name="chevron_right" />
        </button>
      </div>

      <div className="wd-legend">
        <span><i className="wd-swatch today" />Tänään</span>
        <span><i className="wd-swatch ready" />Valmis</span>
        {wordsPerDay > 1 && <span><i className="wd-swatch partial" />Kesken</span>}
        <span><i className="wd-swatch empty" />Tyhjä</span>
        <span><i className="wd-swatch past" />Mennyt</span>
        <button type="button" className="wd-disliked-toggle" onClick={() => setShowDisliked(s => !s)}>
          <Icon name="thumb_down" /> Hylätyt sanat ({dislikedWords.size})
        </button>
      </div>

      {showDisliked && (
        <div className="wd-disliked-panel">
          {dislikedWords.size === 0 ? (
            <span className="wd-disliked-empty">Ei hylättyjä sanoja</span>
          ) : (
            [...dislikedWords].sort((a, b) => a.localeCompare(b, 'fi')).map(w => (
              <span key={w} className="wd-disliked-chip">
                {w}
                <button
                  type="button"
                  onClick={() => handleRemoveDisliked(w)}
                  title="Palauta sana arvontaan"
                  aria-label={`Palauta ${w}`}
                >
                  <Icon name="close" />
                </button>
              </span>
            ))
          )}
        </div>
      )}

      {lastDislike && (
        <div className="wd-undo-toast" role="status">
          Hylätty {lastDislike.word}
          <button type="button" onClick={handleUndoDislike}>Kumoa</button>
        </div>
      )}

      <div className="wd-months">
        {months.map((name, i) => (
          <section
            key={i}
            className={`wd-month ${isCollapsed(i) ? 'collapsed' : ''}`}
            ref={year === currentYear && i === currentMonth ? currentMonthRef : null}
          >
            {!isCollapsed(i) && <h3 className="wd-month-name">{name}</h3>}
            {isCollapsed(i) ? (
              <button type="button" className="btn-secondary wd-load-month" onClick={() => loadMonth(i)}>
                <Icon name="expand_more" /> Load {name.toLowerCase()} {year}
              </button>
            ) : (
            <div className="wd-days">
              {getMonthDays(i).map(date => {
                const dateStr = formatDate(date);
                let wordData = words[dateStr];
                if (wordsPerDay === 1) {
                  wordData = wordData || '';
                } else if (!Array.isArray(wordData)) {
                  wordData = Array(wordsPerDay).fill('');
                }
                const list = wordsPerDay === 1 ? [wordData] : wordData;

                const status = getStatus(dateStr, wordData);
                const canRandomize = status !== 'today' && status !== 'past';
                const isValid = list.every(w => !w || w.length === 5);

                // Quordle days are filled with "roll all" first; per-word buttons wait until there are words
                const idle = wordsPerDay > 1 && status === 'empty';

                return (
                  <div key={dateStr} className={`wd-day ${status} ${!isValid ? 'invalid' : ''} ${idle ? 'is-idle' : ''}`}>
                    <div className="wd-day-head">
                      <span className="wd-day-num">{date.getDate()}</span>
                      {wordsPerDay > 1 && canRandomize && (
                        <button
                          type="button"
                          className="wd-btn wd-dice wd-roll-all"
                          onClick={() => handleRandomizeAll(dateStr)}
                          disabled={isLoadingDict}
                          title="Arvo kaikki sanat"
                          aria-label="Arvo kaikki sanat"
                        >
                          <Icon name="casino" />
                        </button>
                      )}
                    </div>
                    {list.map((w, idx) => renderWord(dateStr, w || '', idx, canRandomize))}
                  </div>
                );
              })}
            </div>
            )}
          </section>
        ))}
      </div>
    </div>
  );
};

export default WordleCalendar;
